import type { Options } from '@Types/Shared/Options';
import { type MenusType, type SiteType } from '@Types/Shared/SiteDatas';

import { useEffect, useState } from 'react';

import date from 'date-and-time';

import apiFetch from '@wordpress/api-fetch';

import { type PostType } from '@/src/Types/Shared/Post';
import { type QueryPost, type QueryType } from '@/src/Types/Shared/Query';
import { type TermType } from '@/src/Types/Shared/Taxonomy';
import Layout from '@/src/Views/Shared/Layout/Layout';
import { __Front, getTranslationsWithoutPLL, parseURL } from '@/src/Views/Shared/lib';
import { router } from '@inertiajs/react';

import ViewCalendar from './components/ViewCalendar';

import 'react-date-range/dist/styles.css'; // main style file
import 'react-date-range/dist/theme/default.css'; // theme css file

import type { EventsOptions } from '@Types/Blocks/Events';
import type {
	EventsFiltersChoices,
	EventsListingOptions,
	EventsViewsChoices,
} from '@Types/Shared/advanced-block-page/EventListing';
import type { BtnOptions } from '@Types/Shared/Button';
import type { ArrowDecorations, SectionDecorationOptions } from '@Types/Shared/Common';

import type { ReactElement } from '@wordpress/element/build-types/serialize';

import { cn } from '@/src/Blocks/_components/ui/utils';
import { getSingleBtnStyle } from '@Views/Shared/GlobalStyles/GlobalStyles';
import {
	ArrowBold,
	ArrowFull,
	ArrowLong,
	ArrowSmall,
	Bubble,
	DateIcon,
	Wave,
} from '@Views/Shared/Icons';
import type { Translation } from '@Views/Shared/Translations';

import CardsTypes from './components/CardsTypes';
import { Filters } from './components/Filters';
import { Pagination } from './components/Pagination';

type ListingEventsProps = {
	post: PostType;
	types: TermType[];
	venues: TermType[];
	organizers: TermType[];
	site: SiteType;
	menus: MenusType;
	minos: Options;
	translations: Translation[];
};

export type FiltersEventsType = {
	venues: TermType[];
	organizers: TermType[];
	types: TermType[];
	setSelectedVenues: (e: string) => void;
	setSelectedOrganizers: (e: string) => void;
	setSelectedTypes: (e: string) => void;
	setSelectedStartDate: (e: string) => void;
	setSelectedEndDate: (e: string) => void;
	selectedVenues: string;
	selectedOrganizers: string;
	selectedTypes: string;
	selectedStartDate: string;
	selectedEndDate: string;
	selectView: string;
	filtersSelection: EventsFiltersChoices;
	resetButtonStyle?: BtnOptions;
	listingEvents?: EventsListingOptions;
	labels?: {
		[x: string]: {
			noFound?: string | undefined;
			filter?: string | undefined;
		};
	};
};

type Props = {
	updateView: (value: 'list' | 'calendar') => void;
	updateMonth: (value: string) => void;
	setPage: (value: number) => void;
	sectionDecoration: SectionDecorationOptions;
	listingEvents: EventsListingOptions;
	filtersSelection: EventsFiltersChoices;
	viewSelector: EventsViewsChoices;
	blockEvents: EventsOptions;
	selectView: string;
	selectedMonth: string;
	results: number;
	page: number;
	maxPage: number;
	posts: PostType[];
	FiltersProps: FiltersEventsType;
	calendar: any;
	minosOptions: Options;
	isLoading: boolean;
};

export default function Listing(props: ListingEventsProps) {
	const { types, venues, organizers, site, menus, minos, translations } = props;

	const listingEvents: EventsListingOptions = { ...minos.layout.events.index };
	const blockEvents: EventsOptions = { ...minos.blocks.events.default };
	const filtersSelection: EventsFiltersChoices = {
		...minos.layout.events.index.listingFiltersChoices,
	};
	const viewSelector = minos.layout.events.index.viewsChoices;
	const sectionDecoration: SectionDecorationOptions = { ...minos.layout.sectionDecoration };

	const params = parseURL(window.location.href);

	const [page, setPage] = useState<number>(params.pg ? params.pg : 1);
	const [posts, setPosts] = useState<PostType[]>([]);
	const [maxPage, setMaxPage] = useState<number>(1);
	const [calendar, setCalendar] = useState<any>([]);
	const [results, setResults] = useState<number>(0);
	const [selectedTypes, setSelectedTypes] = useState<any>(
		params.type_event ? params.type_event : ''
	);
	const [selectedVenues, setSelectedVenues] = useState<any>(params.venue ? params.venue : '');
	const [selectedOrganizers, setSelectedOrganizers] = useState<any>(
		params.organizer ? params.organizer : ''
	);
	const [selectedStartDate, setSelectedStartDate] = useState<any>(params.start ? params.start : '');
	const [selectedEndDate, setSelectedEndDate] = useState<any>(params.end ? params.end : '');
	const [selectView, setSelectView] = useState<string>(params.view ? params.view : 'list');
	const [selectedMonth, setSelectedMonth] = useState<string>('');
	const [selectedM, setSelectedM] = useState<string>(
		params.month ? params.month : date.format(new Date(), 'MM')
	);
	const [selectedYear, setSelectedYear] = useState<string>(
		params.y ? params.y : date.format(new Date(), 'YYYY')
	);
	const [isLoading, setIsLoading] = useState<boolean>(true);

	useEffect(() => {
		if (viewSelector != 'both') {
			setSelectView(viewSelector);
		}
	});

	useEffect(() => {
		setIsLoading(true);
		const _options: Promise<QueryType> = apiFetch({
			path: `/minos/v2/getposts`,
			method: 'POST',
			data: {
				args: {
					post_type: 'event',
					orderby: 'meta_value',
					order: 'ASC',
					meta_key: 'start_date_ymd',
					posts_per_page: 9,
					paged: page,
					taxs: {
						type_event: selectedTypes,
						venue: selectedVenues,
						organizer: selectedOrganizers,
					},
					date_start: selectedStartDate,
					date_end: selectedEndDate,
					year: selectedYear,
					month: selectedM,
					selectView: selectView,
					with_image: true,
					lang: site.lang,
				},
			},
		});
		_options.then((result) => {
			const q = result;

			if (selectView === 'calendar') {
				setCalendar(q.calendar);
				setSelectedMonth(q.month);
				setResults(q.found_posts);
			} else {
				setMaxPage(q.max_num_pages);
				setPosts(q.posts);
				setResults(q.found_posts);
			}
			setIsLoading(false);
		});
	}, [
		selectedVenues,
		selectedTypes,
		selectedOrganizers,
		page,
		selectedStartDate,
		selectedEndDate,
		selectView,
		selectedM,
		selectedYear,
	]);

	useEffect(() => {
		router.get(
			window.location.href,
			{
				venue: selectedVenues,
				organizer: selectedOrganizers,
				type_event: selectedTypes,
				start: selectedStartDate,
				end: selectedEndDate,
				month: selectedM,
				y: selectedYear,
				view: selectView,
			},
			{
				preserveState: true,
				replace: true,
			}
		);
	}, [
		selectedVenues,
		selectedTypes,
		selectedOrganizers,
		selectedStartDate,
		selectedEndDate,
		selectView,
		selectedM,
		selectedYear,
	]);

	const FiltersProps = {
		types,
		venues,
		organizers,
		setSelectedVenues,
		setSelectedTypes,
		setSelectedOrganizers,
		setSelectedStartDate,
		setSelectedEndDate,
		selectedOrganizers,
		selectedVenues,
		selectedTypes,
		selectedStartDate,
		selectedEndDate,
		selectView,
		filtersSelection,
		listingEvents,
		resetButtonStyle: getSingleBtnStyle(minos, listingEvents.resetButtonStyle),
	};

	const updateMonth = (value: string) => {
		const dt = new Date(selectedYear + '-' + selectedM + '-01');

		if (value === '-1') {
			dt.setMonth(dt.getMonth() + -1);
		} else {
			dt.setMonth(dt.getMonth() + +1);
		}

		dt.toISOString();
		const year = date.format(dt, 'YYYY');
		const m = date.format(dt, 'MM');
		const month = date.format(dt, 'MMMM');

		setSelectedM(m);
		setSelectedMonth(month);
		setSelectedYear(year);
	};

	const updateView = (value: 'list' | 'calendar') => {
		setSelectView(value);
	};

	const viewProps: Props = {
		sectionDecoration,
		listingEvents,
		filtersSelection,
		FiltersProps,
		results,
		viewSelector,
		selectView,
		updateView,
		posts,
		blockEvents,
		page,
		maxPage,
		setPage,
		updateMonth,
		selectedMonth,
		calendar,
		minosOptions: minos,
		isLoading,
	};

	return (
		<Layout
			blocks={['listing-events', 'events']}
			seo={null}
			site={site}
			menus={menus}
			minos={minos}
			translations={translations}
			className={
				listingEvents.variant === 'block' ? 'relative bg-minos-listing-events-secondary-color' : ''
			}
		>
			<ListingEventsView {...viewProps} />
		</Layout>
	);
}

export const ListingEventsView = ({
	sectionDecoration,
	listingEvents,
	filtersSelection,
	FiltersProps,
	results,
	viewSelector,
	selectView,
	updateView,
	posts,
	blockEvents,
	page,
	maxPage,
	setPage,
	updateMonth,
	selectedMonth,
	calendar,
	minosOptions,
	isLoading,
}: Props) => {
	const labels = {
		types: {
			noFound: filtersSelection.typesNotFoundLabel,
			filter: filtersSelection.typesFilterLabel,
		},
		organizers: {
			noFound: filtersSelection.organizersNotFoundLabel,
			filter: filtersSelection.organizersFilterLabel,
		},
		venues: {
			noFound: filtersSelection.venuesNotFoundLabel,
			filter: filtersSelection.venuesFilterLabel,
		},
		dates: {
			filter: filtersSelection.datesFilterLabel,
		},
	};

	const displayArrowIcon = (paginationArrow: ArrowDecorations, place: 'right' | 'left') => {
		let element: ReactElement;

		const style = cn(
			'hover:duration-300 group-hover:ease-in-out text-minos-listing-events-pagination-arrow-color hover:text-minos-listing-events-pagination-arrow-color-hover focus:text-minos-listing-events-pagination-arrow-color-hover',
			place === 'left' ? '-rotate-180 hover:-translate-x-2' : 'hover:translate-x-2'
		);

		if (paginationArrow) {
			switch (paginationArrow) {
				case 'full':
					element = <ArrowFull className={style} />;
					break;
				case 'bold':
					element = <ArrowBold className={style} />;
					break;
				case 'long':
					element = <ArrowLong width='30' className={style} />;
					break;
				case 'short':
					element = <ArrowSmall className={style} />;
					break;
				default:
					element = <ArrowLong width='30' className={style} />;
			}
			return element;
		}

		return null;
	};

	return (
		<article
			data-listing={`listing-layout-${listingEvents.variant}`}
			className={`relative overflow-hidden ${listingEvents.variant === 'block' ? 'bg-minos-listing-events-secondary-color' : ''}`}
		>
			{/* Bubble decoration */}
			{sectionDecoration?.isSectionDecorated && sectionDecoration.type === 'bubble' && (
				<>
					<Bubble
						position='left'
						className='minos-decoration-bubble-left top-[-250px]'
						pathClassName='fill-minos-decoration-bubble-left'
					/>
				</>
			)}
			<div
				className={`relative ${
					listingEvents.variant === 'block' &&
					`z-[11] mx-auto w-11/12 rounded-minos-listing-events-container border-minos-listing-events-container-border-width border-minos-listing-events-border-color ${
						sectionDecoration?.isSectionDecorated && sectionDecoration?.type === 'wave'
							? 'mb-12 mt-7 tablet:mb-24 tablet:mt-12 desktop:mb-32'
							: 'my-7 tablet:my-12'
					}`
				} overflow-hidden bg-minos-listing-events-bg-color`}
			>
				<header
					data-heading={`listing-heading-${listingEvents.headingVariant}`}
					className={`minos-grid relative z-[12] ${
						sectionDecoration?.isSectionDecorated &&
						sectionDecoration?.type === 'wave' &&
						listingEvents.variant === 'headline'
							? 'mb-12 tablet:mb-24'
							: listingEvents.variant === 'headline'
								? 'mb-12'
								: ''
					} ${listingEvents.variant === 'headline' ? 'bg-minos-listing-events-secondary-color' : ''}`}
				>
					<div
						className={`relative z-[11] col-start-2 col-end-14 flex flex-col 
	${listingEvents.headingVariant === 'center' ? 'items-center' : listingEvents.headingVariant === 'centerInline' ? 'items-center' : 'items-start'} ${
		sectionDecoration?.isSectionDecorated &&
		sectionDecoration?.type === 'wave' &&
		listingEvents.variant === 'headline'
			? 'py-12 tablet:pb-28 desktop:pb-32'
			: 'py-12'
	}`}
					>
						<h1 className='minos-listing-events-heading-title-font-style z-[11] mb-4 text-4xl tablet:minos-listing-events-heading-title-font-style tablet:mb-8'>
							{getTranslationsWithoutPLL(
								listingEvents.listingHeadingTitle,
								__Front('front.listingEvents')
							)}
						</h1>

						<div
							className={`z-[11] flex flex-wrap gap-4 tablet:gap-8 ${listingEvents.headingVariant === 'center' ? 'flex-col items-center' : listingEvents.headingVariant === 'centerInline' ? 'flex-col-reverse items-center tablet:w-full tablet:flex-row tablet:justify-between' : 'w-full flex-col-reverse justify-between tablet:flex-row tablet:items-center'}`}
						>
							{listingEvents.headingVariant != 'center' && (
								<>
									{!isLoading && (
										<>
											{results && results > 0 ? (
												<p className='minos-listing-events-result-font-style text-base tablet:minos-listing-events-result-font-style'>
													{results} {__Front('front.results')}
												</p>
											) : (
												<p className='minos-listing-events-result-font-style text-base tablet:minos-listing-events-result-font-style'>
													{results} {__Front('front.result')}
												</p>
											)}
										</>
									)}
								</>
							)}

							{filtersSelection.venues ||
							filtersSelection.types ||
							filtersSelection.organizers ||
							filtersSelection.dates ? (
								<>
									<Filters {...FiltersProps} labels={labels} />
								</>
							) : (
								''
							)}

							{listingEvents.headingVariant === 'center' && (
								<>
									<>
										{!isLoading && (
											<>
												{results && results > 0 ? (
													<p className='minos-listing-events-result-font-style text-base tablet:minos-listing-events-result-font-style'>
														{results} {__Front('front.results')}
													</p>
												) : (
													<p className='minos-listing-events-result-font-style text-base tablet:minos-listing-events-result-font-style'>
														{results} {__Front('front.result')}
													</p>
												)}
											</>
										)}
									</>
								</>
							)}
						</div>
					</div>

					{/* Wave Decoration only visible if variant listing === heading */}
					{listingEvents.variant === 'headline' && (
						<>
							{sectionDecoration.isSectionDecorated && sectionDecoration.type === 'wave' ? (
								<Wave
									className='absolute bottom-0 left-0 z-10 h-[17px] w-screen overflow-x-hidden tablet:h-[40px] desktop:h-[70px]'
									pathClassName='fill-minos-listing-events-bg-color'
								/>
							) : (
								''
							)}
						</>
					)}
				</header>

				<div className='minos-grid relative z-10'>
					<div className='col-start-2 col-end-14 mb-8 flex items-center justify-between'>
						{viewSelector === 'both' && (
							<div className='flex overflow-hidden rounded-minos-events-filters-radius border-minos-events-filters-border-width border-minos-listing-events-filters-actif-color'>
								<div
									className={`relative ${
										selectView === 'list'
											? 'minos-listing-events-filters-font-style bg-minos-listing-events-filters-actif-color'
											: 'minos-listing-events-filters-font-style bg-minos-listing-events-filters-bg-color'
									}`}
								>
									<button
										title={
											selectView === 'list' ? __Front('front.active_list view') : __Front('front.list_view')
										}
										onClick={() => updateView('list')}
										className='flex h-full cursor-pointer items-center justify-center p-4'
										aria-pressed={selectView === 'list' ? true : false}
									>
										<span className='sr-only'>{__Front('front.list_view')}</span>
										<svg
											width='20'
											height='12'
											viewBox='0 0 20 12'
											fill='none'
											xmlns='http://www.w3.org/2000/svg'
											aria-hidden
											className='text-minos-listing-events-filters-color block'
										>
											<path
												d='M1.71023 10.2899C1.61513 10.1988 1.50298 10.1275 1.38023 10.0799C1.13677 9.97986 0.863694 9.97986 0.620232 10.0799C0.497481 10.1275 0.385336 10.1988 0.290233 10.2899C0.199192 10.385 0.127827 10.4971 0.0802325 10.6199C0.00365307 10.802 -0.0172696 11.0027 0.0201045 11.1967C0.0574787 11.3907 0.151475 11.5693 0.290233 11.7099C0.387433 11.7982 0.499048 11.8692 0.620232 11.9199C0.739933 11.9728 0.869362 12.0001 1.00023 12.0001C1.1311 12.0001 1.26053 11.9728 1.38023 11.9199C1.50142 11.8692 1.61303 11.7982 1.71023 11.7099C1.84899 11.5693 1.94299 11.3907 1.98036 11.1967C2.01773 11.0027 1.99681 10.802 1.92023 10.6199C1.87264 10.4971 1.80127 10.385 1.71023 10.2899ZM5.00023 1.99988H19.0002C19.2654 1.99988 19.5198 1.89452 19.7073 1.70698C19.8949 1.51945 20.0002 1.26509 20.0002 0.999878C20.0002 0.734661 19.8949 0.480308 19.7073 0.292771C19.5198 0.105235 19.2654 -0.00012207 19.0002 -0.00012207H5.00023C4.73502 -0.00012207 4.48066 0.105235 4.29313 0.292771C4.10559 0.480308 4.00023 0.734661 4.00023 0.999878C4.00023 1.26509 4.10559 1.51945 4.29313 1.70698C4.48066 1.89452 4.73502 1.99988 5.00023 1.99988ZM1.71023 5.28988C1.56961 5.15112 1.39104 5.05712 1.19705 5.01975C1.00306 4.98238 0.802343 5.0033 0.620232 5.07988C0.499048 5.13052 0.387433 5.20155 0.290233 5.28988C0.199192 5.38498 0.127827 5.49713 0.0802325 5.61988C0.0273273 5.73958 0 5.86901 0 5.99988C0 6.13075 0.0273273 6.26018 0.0802325 6.37988C0.130874 6.50106 0.201902 6.61268 0.290233 6.70988C0.387433 6.79821 0.499048 6.86924 0.620232 6.91988C0.739933 6.97278 0.869362 7.00011 1.00023 7.00011C1.1311 7.00011 1.26053 6.97278 1.38023 6.91988C1.50142 6.86924 1.61303 6.79821 1.71023 6.70988C1.79856 6.61268 1.86959 6.50106 1.92023 6.37988C1.97314 6.26018 2.00047 6.13075 2.00047 5.99988C2.00047 5.86901 1.97314 5.73958 1.92023 5.61988C1.87264 5.49713 1.80127 5.38498 1.71023 5.28988ZM19.0002 4.99988H5.00023C4.73502 4.99988 4.48066 5.10524 4.29313 5.29277C4.10559 5.48031 4.00023 5.73466 4.00023 5.99988C4.00023 6.26509 4.10559 6.51945 4.29313 6.70698C4.48066 6.89452 4.73502 6.99988 5.00023 6.99988H19.0002C19.2654 6.99988 19.5198 6.89452 19.7073 6.70698C19.8949 6.51945 20.0002 6.26509 20.0002 5.99988C20.0002 5.73466 19.8949 5.48031 19.7073 5.29277C19.5198 5.10524 19.2654 4.99988 19.0002 4.99988ZM1.71023 0.289878C1.61513 0.198837 1.50298 0.127472 1.38023 0.0798779C1.19812 0.00329842 0.997409 -0.017624 0.803419 0.0197501C0.609429 0.0571242 0.430856 0.15112 0.290233 0.289878C0.201902 0.387079 0.130874 0.498693 0.0802325 0.619878C0.0273273 0.739578 0 0.869007 0 0.999878C0 1.13075 0.0273273 1.26018 0.0802325 1.37988C0.130874 1.50106 0.201902 1.61268 0.290233 1.70988C0.387433 1.79821 0.499048 1.86924 0.620232 1.91988C0.802343 1.99646 1.00306 2.01738 1.19705 1.98001C1.39104 1.94263 1.56961 1.84864 1.71023 1.70988C1.79856 1.61268 1.86959 1.50106 1.92023 1.37988C1.97314 1.26018 2.00047 1.13075 2.00047 0.999878C2.00047 0.869007 1.97314 0.739578 1.92023 0.619878C1.86959 0.498693 1.79856 0.387079 1.71023 0.289878ZM19.0002 9.99988H5.00023C4.73502 9.99988 4.48066 10.1052 4.29313 10.2928C4.10559 10.4803 4.00023 10.7347 4.00023 10.9999C4.00023 11.2651 4.10559 11.5194 4.29313 11.707C4.48066 11.8945 4.73502 11.9999 5.00023 11.9999H19.0002C19.2654 11.9999 19.5198 11.8945 19.7073 11.707C19.8949 11.5194 20.0002 11.2651 20.0002 10.9999C20.0002 10.7347 19.8949 10.4803 19.7073 10.2928C19.5198 10.1052 19.2654 9.99988 19.0002 9.99988Z'
												fill='currentColor'
											/>
										</svg>
									</button>
								</div>

								<div
									className={`relative ${
										selectView === 'calendar'
											? 'minos-listing-events-filters-font-style bg-minos-listing-events-filters-actif-color'
											: 'minos-listing-events-filters-font-style bg-minos-listing-events-filters-bg-color'
									}`}
								>
									<button
										title={
											selectView === 'calendar'
												? __Front('front.active_calendar_view')
												: __Front('front.calendar_view')
										}
										onClick={() => updateView('calendar')}
										className='flex h-full cursor-pointer items-center justify-center p-4'
										aria-pressed={selectView === 'calendar' ? true : false}
									>
										<span className='sr-only'>{__Front('front.calendar_view')}</span>
										<DateIcon width='20' height='20' className=' block' />
									</button>
								</div>
							</div>
						)}
					</div>
				</div>

				{selectView != 'calendar' ? (
					<section className='minos-grid relative' data-variant={blockEvents.variant}>
						<div className='z-[11] col-start-2 col-end-14 grid grid-cols-4 gap-y-5 tablet:grid-cols-12 tablet:gap-8'>
							{isLoading ? (
								<div className='col-span-12 flex justify-center p-10'>
									<div className='size-10 animate-spin rounded-full border-4 border-gray-300 border-t-black p-5' />
								</div>
							) : (
								<>
									{posts && posts.length > 0 ? (
										<>
											{posts.map((post: QueryPost) => (
												<CardsTypes
													key={post.ID}
													post={post}
													cardType={blockEvents.cardType}
													textLabel={getTranslationsWithoutPLL(
														listingEvents.cardTextLabel,
														__Front('front.events.readmore')
													)}
													variant={blockEvents.variant}
													iconType={blockEvents.iconType}
													displayDate={blockEvents.displayDate}
													displayExcerpt={blockEvents.displayExcerpt}
													displayOrganizer={blockEvents.displayOrganizer}
													displayPlace={blockEvents.displayPlace}
													displayPrice={blockEvents.displayPrice}
													displayTag={blockEvents.displayTag}
													readMoreStyle={getSingleBtnStyle(minosOptions, blockEvents.readMoreStyle)}
												/>
											))}

											<Pagination
												className={`${
													sectionDecoration?.isSectionDecorated &&
													sectionDecoration?.type === 'wave' &&
													listingEvents.variant != 'block'
														? 'pb-20 desktop:pb-36'
														: 'pb-12'
												}`}
												variant={listingEvents.paginationVariant}
												paginationArrow={listingEvents.paginationArrow}
												page={page}
												setPage={setPage}
												maxPage={maxPage}
											/>
										</>
									) : (
										<div className='minos-listing-events-result-font-style col-span-12 my-10 text-center text-base tablet:minos-listing-events-result-font-style'>
											{__Front('front.no_post_found')}
										</div>
									)}
								</>
							)}
						</div>
						{listingEvents.variant != 'block' && (
							<>
								{/* Bubble decoration */}
								{sectionDecoration?.isSectionDecorated && sectionDecoration.type === 'bubble' && (
									<>
										<Bubble
											position='right'
											className='minos-decoration-bubble-right'
											pathClassName='fill-minos-decoration-bubble-right'
										/>
									</>
								)}
							</>
						)}
					</section>
				) : (
					<>
						<nav className='minos-grid relative z-[11] flex gap-4'>
							<div className='col-start-2 col-end-14 flex gap-4'>
								<button onClick={() => updateMonth('-1')}>
									<span className='sr-only'>{__Front('front.events.previous_month')}</span>

									{listingEvents.paginationArrow && displayArrowIcon(listingEvents.paginationArrow, 'left')}
								</button>
								<p className='minos-listing-events-pagination-font-style'>{selectedMonth}</p>
								<button value='+1' onClick={() => updateMonth('+1')}>
									<span className='sr-only'>{__Front('front.next_month')}</span>
									{listingEvents.paginationArrow && displayArrowIcon(listingEvents.paginationArrow, 'right')}
								</button>
							</div>
						</nav>
						<div className='minos-grid relative'>
							<ViewCalendar calendar={calendar} />
							{listingEvents.variant != 'block' && (
								<>
									{/* Bubble decoration */}
									{sectionDecoration?.isSectionDecorated && sectionDecoration.type === 'bubble' && (
										<>
											<Bubble
												position='right'
												className='minos-decoration-bubble-right'
												pathClassName='fill-minos-decoration-bubble-right'
											/>
										</>
									)}
								</>
							)}
						</div>
					</>
				)}
			</div>

			{listingEvents.variant === 'block' && (
				<>
					{/* Bubble decoration */}
					{sectionDecoration?.isSectionDecorated && sectionDecoration.type === 'bubble' && (
						<>
							<Bubble
								position='right'
								className='minos-decoration-bubble-right'
								pathClassName='fill-minos-decoration-bubble-right'
							/>
						</>
					)}
				</>
			)}
		</article>
	);
};
